import { Grid, MenuItem } from '@material-ui/core';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { useObservable } from 'react-use-observable';
import { Space } from 'v2/components/Shared/Space';
import useBoolean from 'v2/hooks/useBoolean';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { ReactComponent as SelfieSvg } from './selfie.svg';
import { useStyles } from './styles';
import { FileInput } from '../FileInput';
import { Input } from '../Input';
import { Layout } from '../Layout';
import { Modal } from '../Modal';

import type { NewBankAccount } from '../form';

function Documents() {
  const classes = useStyles();
  const { values } = useFormikContext<NewBankAccount>();
  const [isRgModalOpen, setIsRgModalState] = useBoolean(false);
  const [isSelfieModalOpen, setIsSelfieModalState] = useBoolean(false);
  const [isCnhModalOpen, setIsCnhModalState] = useBoolean(false);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const isCrossborder = user?.international_account || user?.crossborder;

  if (!user) return null;

  return (
    <>
      <Layout
        title='Documentos'
        description='Agora precisamos que você nos envie uma foto de um documento que contenha o número do seu CPF, pode ser seu RG ou CNH'
      >
        <Grid container spacing={2} className={classes.content}>
          <Grid item xs={12} sm={10}>
            <Input
              select
              name='personal_document'
              label='Selecione o documento pessoal*'
              helperText={
                values.personal_document === 'RG' ? 'Seu RG precisa conter o número do seu CPF' : 'Sua CNH precisa conter o número do seu CPF'
              }
            >
              <MenuItem value='RG'>RG</MenuItem>
              <MenuItem value='CNH'>CNH</MenuItem>
            </Input>
          </Grid>
          {values.personal_document === 'RG' && (
            <>
              <Grid item xs={12} sm={10}>
                <FileInput
                  name='rg_front_image'
                  label='Enviar foto do RG - Frente*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText={
                    <span id='rg-front-image-example-btn' className={classes.openModalBtn} role='button' onClick={setIsRgModalState.toTrue}>
                      Exemplo da frente RG: <strong>Ver exemplo</strong>
                    </span>
                  }
                />
              </Grid>
              <Grid item xs={12} sm={10}>
                <FileInput
                  name='rg_back_image'
                  label='Enviar foto do RG - Verso*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText={
                    <span id='rg-back-image-example-btn' className={classes.openModalBtn} role='button' onClick={setIsRgModalState.toTrue}>
                      Exemplo do verso RG: <strong>Ver exemplo</strong>
                    </span>
                  }
                />
              </Grid>
            </>
          )}
          {values.personal_document === 'CNH' && (
            <Grid item xs={12} sm={10}>
              {isCrossborder ? (
                <FileInput
                  name='cnh_image'
                  label='Enviar foto do AOI/EIN*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText='Documento de Constituição de Empresa (Article of Incorporation) ou do Employer Identification Number (EIN) expedido pelo IRS'
                />
              ) : (
                <FileInput
                  name='cnh_image'
                  label='Enviar foto da CNH*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText={
                    <span id='cnh-image-example-btn' className={classes.openModalBtn} role='button' onClick={setIsCnhModalState.toTrue}>
                      Exemplo da CNH: <strong>Ver exemplo</strong>
                    </span>
                  }
                />
              )}
            </Grid>
          )}
          <Grid item xs={12}>
            <p className={classes.helperText}>O arquivo deve ter no máximo 2MB e estar no formato .jpg, .jpeg, .png ou .pdf</p>
          </Grid>
          <Grid item xs={12}>
            <h3 className={classes.subtitle}>Agora um vídeo de validação</h3>
            <p className={classes.subtitleDescription}>
              Falta pouco! Para validar sua identidade precisamos que você nos envie um vídeo segurando seu documento e lembre-se em gravar no modo
              selfie
            </p>
          </Grid>
          <Grid item xs={12} sm={10}>
            <FileInput
              name='selfie_video'
              label='Enviar vídeo*'
              helperText={
                <>
                  <span id='selfie-video-example-btn' className={classes.openModalBtn} role='button' onClick={setIsSelfieModalState.toTrue}>
                    Exemplo do vídeo selfie: <strong>Ver exemplo</strong>
                  </span>
                  <Space axis='vertical' size={8} />
                  <span className={classes.helperText}>É rapidinho! Um vídeo de 3 a 7 segundos de duração e no máximo 35MB já é o suficiente</span>
                </>
              }
              validFormats='video/avi, video/x-msvideo, video/mp4, video/quicktime, video/x-ms-wmv, video/x-matroska, video/webm'
            />
          </Grid>
        </Grid>
        <Modal isOpen={isRgModalOpen} handleClose={setIsRgModalState.toFalse}>
          <div className={classes.rgModalContent}>
            <section>
              <p>
                Verso do RG <span>(com CPF)</span>
              </p>
              <img src='/img/back-rg.jpg' alt='Verso do RG com CPF - exemplo' />
            </section>
            <section>
              <p>Frente do RG</p>
              <img src='/img/front-rg.jpg' alt='Frente do RG com CPF - exemplo' />
            </section>
          </div>
        </Modal>
        <Modal isOpen={isSelfieModalOpen} handleClose={setIsSelfieModalState.toFalse}>
          <div className={classes.selfieModalContent}>
            <p>Segure seu documento e grave um vídeo</p>
            <SelfieSvg />
          </div>
        </Modal>
        <Modal isOpen={isCnhModalOpen} handleClose={setIsCnhModalState.toFalse}>
          <div className={classes.cnhModalContent}>
            <p>CNH - exemplo</p>
            <img src='/img/cnh.jpg' alt='CNH - exemplo' />
          </div>
        </Modal>
      </Layout>
    </>
  );
}

export { Documents };
